import React, { FC, useMemo, useContext } from 'react';
import { ThemeContext } from 'styled-components';

import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import findChildByType from '../../../utils/components/_findChildByType';
import excludeChildrenByType from '../../../utils/components/_excludeChildrenByType';

import Dialog from '../Dialog';
import { ModalProps } from '../Modal';

export interface ErrorModalProps extends ModalProps<{}> {
  /** Error message. If an object, it should contain a `message` prop. */
  error?: string | { message: string };
}

interface ErrorModalStatics {
  Content: typeof Dialog.Content;
  Footer: typeof Dialog.Footer;
  publicProps: StringKeyOf<ErrorModalProps>[];
}

/**
 * @since 0.21.0
 */
const ErrorModal: FC<ErrorModalProps> & ErrorModalStatics = ({
  name,
  children,
  error,
  modalTitle,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const content = excludeChildrenByType(children, Dialog.Footer);
  const footer = findChildByType(children, Dialog.Footer);

  const errorMessage = useMemo(() => {
    if (typeof error === 'string') {
      return error;
    }

    if (typeof error === 'object' && error !== null) {
      return error.message;
    }

    // eslint-disable-next-line react/no-array-index-key
    return content.map((child, index) => <React.Fragment key={index}>{typeof child === 'function' ? null : child}</React.Fragment>);
  }, [content, error]);

  if (typeof children === 'function') {
    return (
      <Dialog
        name={name}
        modalTitle={modalTitle}
        Icon={theme?.icons?.error}
        color={theme.colors.error}
        {...props}
      >
        {children}
      </Dialog>
    );
  }

  return (
    <Dialog
      name={name}
      modalTitle={modalTitle}
      Icon={theme?.icons?.error}
      color={theme.colors.error}
      {...props}
    >
      <Dialog.Content>{errorMessage}</Dialog.Content>
      {footer}
    </Dialog>
  );
};

ErrorModal.Content = Dialog.Content;
ErrorModal.Footer = Dialog.Footer;
ErrorModal.publicProps = ['error', ...Dialog.publicProps];

export default ErrorModal;
